import { actionProps } from "@/lib/controlAnalytics";
import { buttonClass } from "@/components/common/controls";
import { ArrowUpRightIcon, CheckIcon, CloseIcon, DownloadIcon } from "@/components/icons";
import { SaveButton } from "@/components/site/PostingActions";
import type { Posting } from "@/lib/api/catalogue";
import type { Application } from "@/lib/api/envoy";
import { SOURCE_NAME } from "@/lib/applications";
import { canMarkSent, type Sent } from "@/lib/posting";
import { webUrl } from "@/lib/safeUrl";

/**
 * Apply on their site: the posting's apply page, or the posting itself
 * where it gives none. `section` says which of the page's two it is, for
 * the click's event: none for the one under the heading, `foot` for the
 * one at the end of the posting's text.
 */
export function ApplyOnTheirSite({ posting, section }: { posting: Posting; section?: string }) {
    return (
        <a
            {...actionProps("posting_source_open_clicked", {
                source: "PostingDetail",
                posting_id: posting.posting_id,
                option: "apply",
                ...(section ? { section } : {}),
            })}
            href={webUrl(posting.apply_url) ?? webUrl(posting.url)}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonClass("primary", "page", "gap-1.5")}
        >
            Apply on their site
            <ArrowUpRightIcon className="size-3.5" />
        </a>
    );
}

/**
 * Apply on their site, Save, and Open on the source where that is a different page; then the PDF that
 * went once applied, or Applied and Skip before.
 */
export default function PostingButtons({
    posting,
    application,
    applied,
    sent,
    showSave,
    showSkip,
    onOpenPdf,
    onSent,
    onSkip,
}: {
    posting: Posting;
    application: Application | null;
    applied: boolean;
    sent: Sent | null;
    /** Save waits until the sign in is known. */
    showSave: boolean;
    showSkip: boolean;
    onOpenPdf: (resumeId: string) => void;
    onSent: (application: Application | null) => void;
    onSkip: () => void;
}) {
    const applyAt = webUrl(posting.apply_url) ?? webUrl(posting.url);
    return (
        <div className="flex flex-wrap gap-2">
            {!applied && !posting.closed_at && <ApplyOnTheirSite posting={posting} />}
            {showSave && (
                <SaveButton postingId={posting.posting_id} title={posting.title} size="page" />
            )}
            {webUrl(posting.url) !== applyAt && (
                <a
                    {...actionProps("posting_source_open_clicked", {
                        source: "PostingDetail",
                        posting_id: posting.posting_id,
                        option: posting.source,
                    })}
                    href={webUrl(posting.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={buttonClass("secondary", "page", "gap-1.5 border-line-control")}
                >
                    Open on {SOURCE_NAME[posting.source] ?? "their site"}
                    <ArrowUpRightIcon className="size-3.5" />
                </a>
            )}
            {applied ? (
                sent?.resumeId && (
                    <button
                        {...actionProps("tailored_pdf_open_clicked", {
                            source: "PostingDetail",
                            resume_id: sent.resumeId,
                            posting_id: posting.posting_id,
                        })}
                        type="button"
                        onClick={() => onOpenPdf(sent.resumeId as string)}
                        className={buttonClass("primary", "page", "gap-1.5")}
                    >
                        <DownloadIcon className="size-3.5" strokeWidth={2.2} />
                        Download the PDF that went
                    </button>
                )
            ) : (
                <>
                    {canMarkSent(application) && (
                        <button
                            {...actionProps("mark_sent_opened", {
                                source: "PostingDetail",
                                application_id: application?.application_id,
                            })}
                            type="button"
                            onClick={() => onSent(application)}
                            className={buttonClass("secondary", "page", "gap-1.5")}
                        >
                            <CheckIcon className="size-3.5" strokeWidth={2.4} />
                            Mark applied
                        </button>
                    )}
                    {showSkip && (
                        <button
                            {...actionProps("skip_opened", {
                                source: "PostingDetail",
                                posting_id: posting.posting_id,
                            })}
                            type="button"
                            onClick={onSkip}
                            className={buttonClass("ghost", "page", "gap-1.5 px-3.5")}
                        >
                            <CloseIcon className="size-3.5" />
                            Skip
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
